import { useTraduccion } from '@tallerya/i18n';
import { AvisoError, CampoAreaTexto, CampoNumero, useAvisos, useFormulario, Ventana } from '@tallerya/ui';
import { useEffect, useRef, useState } from 'react';
import { numeroDe } from '../productos-servicios/datos';
import { montoDe, useCambiosMontos, useClaveDeReintento, version, type LineaOrden, type MontosOrden } from './datos';
import { Pie } from './VentanasDeLinea';

// Las ventanas de los montos (#137): el precio de una línea y el total a mano.
// Piden `order.amount.edit`. No tienen frame en el Figma: siguen a las de la
// sección «Trabajo» (`VentanasDeLinea`).
//
// En las dos, **vacío es «sin valor»**: el precio vacío deja la línea sin precio,
// y el total vacío lo devuelve a seguir la suma de las líneas.

/**
 * El tope de `numeric(18,6)` sin los decimales, como `precioInvalido` del
 * catálogo: el exacto no cabe en un `number`. Lo que pase de acá lo rechaza la API.
 */
const MONTO_MAXIMO = 999_999_999_999;

/**
 * El error de un monto escrito, o nada si sirve: no negativo, con seis decimales
 * como mucho. Lo usa también el gasto (#143), que tiene la misma columna.
 */
export function errorDeMonto<K extends string>(campo: K, texto: string): Partial<Record<K, string>> {
  const n = numeroDe(texto);
  if (n === null) return {};
  if (Number.isNaN(n)) return { [campo]: 'validation.format' } as Partial<Record<K, string>>;
  if (n < 0) return { [campo]: 'validation.too_small' } as Partial<Record<K, string>>;
  if (n > MONTO_MAXIMO) return { [campo]: 'validation.too_large' } as Partial<Record<K, string>>;
  return Number(n.toFixed(6)) === n ? {} : ({ [campo]: 'validation.format' } as Partial<Record<K, string>>);
}

/** Cómo se escribe un monto que ya existe: `45`, `45.5`. Vacío si no hay. */
const textoDe = (v: number | string | null) => (montoDe(v) === null ? '' : String(montoDe(v)));

/** El total del campo: fijado a mano, se edita lo fijado; si sigue la suma, se arranca vacío. */
const totalDe = (montos: MontosOrden) => (montos.totalIsOverridden ? textoDe(montos.totalAmount) : '');

// ------------------------------------------------------------ El precio de una línea

interface PropsPrecio {
  orden: string;
  linea: LineaOrden | null;
  /** El precio que tiene hoy, de los montos. */
  precio: number | string | null;
  onCerrar: () => void;
}

export function PrecioLinea({ linea, ...props }: PropsPrecio) {
  if (!linea) return null;
  return <ContenidoPrecio key={String(linea.id)} linea={linea} {...props} />;
}

function ContenidoPrecio({ orden, linea, precio, onCerrar }: PropsPrecio & { linea: LineaOrden }) {
  const { t } = useTraduccion();
  const { avisar } = useAvisos();
  const cambios = useCambiosMontos(orden);
  const claveDe = useClaveDeReintento();
  // Cuántas veces se recargó (#171). El precio no viaja con versión, así que es lo
  // que distingue el pedido de después de recargar: con la misma clave, la API
  // devolvería el 409 que guardó del primero.
  const [lectura, setLectura] = useState(0);

  const form = useFormulario<{ unitPrice: string }>({
    inicial: { unitPrice: textoDe(precio) },
    validar: (v) => errorDeMonto('unitPrice', v.unitPrice),
    guardar: (v) => {
      const nuevo = numeroDe(v.unitPrice);
      return cambios.cambiarPrecio(claveDe({ linea: linea.id, nuevo, lectura }), String(linea.id), nuevo);
    },
    camposPorCodigo: { 'service_order.monto_fuera_de_rango': 'unitPrice' },
    recargar: () => void recargar(),
  });

  /** Trae el precio que dejó la otra persona; la ventana sigue abierta, lista para guardar. */
  async function recargar() {
    try {
      const montos = await cambios.leerMontos();
      setLectura((n) => n + 1);
      form.reiniciar({ unitPrice: textoDe(montos.lines.find((m) => String(m.id) === String(linea.id))?.unitPrice ?? null) });
    } catch {
      // Sin red: el aviso sigue ahí, con su «Recargar» para otro intento.
    }
  }

  async function guardar() {
    if (!(await form.enviar())) return;
    avisar({ titulo: t('admin.ordenes.aviso.guardada') });
    onCerrar();
  }

  return (
    <Ventana
      abierta
      titulo={t('admin.ordenes.montos.cambiarPrecio')}
      descripcion={linea.catalogItemName ?? linea.rawText}
      acciones={<Pie guardando={form.guardando} onCerrar={onCerrar} onGuardar={() => void guardar()} />}
    >
      {form.aviso && <AvisoError aviso={form.aviso} />}
      <CampoNumero
        form={form}
        nombre="unitPrice"
        etiqueta={t('admin.ordenes.montos.precioUnitario')}
        info={t('admin.ordenes.montos.precioInfo')}
        decimales={6}
        ancho="corto"
      />
    </Ventana>
  );
}

// ------------------------------------------------------------ El total a mano

interface PropsTotal {
  orden: string;
  montos: MontosOrden;
  /** Si la orden está en un estado final: entonces el motivo es obligatorio. */
  finalizada: boolean;
  abierta: boolean;
  onCerrar: () => void;
}

export function FijarTotal({ abierta, ...props }: PropsTotal) {
  if (!abierta) return null;
  return <ContenidoTotal {...props} />;
}

function ContenidoTotal({ orden, montos, finalizada, onCerrar }: Omit<PropsTotal, 'abierta'>) {
  const { t } = useTraduccion();
  const { avisar } = useAvisos();
  const cambios = useCambiosMontos(orden);
  const claveDe = useClaveDeReintento();
  // La versión de al abrir, no la de cada dibujado (#171): los montos llegan vivos
  // de la ficha, y tras un 409 la recarga traería la versión nueva sin que nadie
  // vea el total que dejó la otra persona. Solo «Recargar» la cambia.
  const [versionLeida, setVersionLeida] = useState(() => version(montos.version));
  const recargarActual = useRef<() => Promise<void>>(() => Promise.resolve());

  const form = useFormulario<{ totalAmount: string; reason: string }>({
    inicial: { totalAmount: totalDe(montos), reason: '' },
    validar: (v) => errorDeMonto('totalAmount', v.totalAmount),
    guardar: (v) => {
      const total = numeroDe(v.totalAmount);
      return cambios.fijarTotal(claveDe({ versionLeida, total, motivo: v.reason }), versionLeida, total, v.reason);
    },
    camposPorCodigo: { 'service_order.total_sin_motivo': 'reason' },
    // El aviso guarda la función del dibujado en que se envió: por la referencia,
    // «Recargar» usa la del último, que conoce el motivo escrito después del 409.
    recargar: () => void recargarActual.current(),
  });

  /** Trae el total que dejó la otra persona, con su versión. El motivo escrito se conserva. */
  async function recargar() {
    try {
      const nuevos = await cambios.leerMontos();
      setVersionLeida(version(nuevos.version));
      form.reiniciar({ totalAmount: totalDe(nuevos), reason: form.valores.reason });
    } catch {
      // Sin red: el aviso sigue ahí, con su «Recargar» para otro intento.
    }
  }

  useEffect(() => {
    recargarActual.current = recargar;
  });

  async function guardar() {
    if (!(await form.enviar())) return;
    avisar({ titulo: t('admin.ordenes.aviso.guardada') });
    onCerrar();
  }

  return (
    <Ventana
      abierta
      titulo={t('admin.ordenes.montos.cambiarTotal')}
      acciones={<Pie guardando={form.guardando} onCerrar={onCerrar} onGuardar={() => void guardar()} />}
    >
      {form.aviso && <AvisoError aviso={form.aviso} />}
      <CampoNumero
        form={form}
        nombre="totalAmount"
        etiqueta={t('admin.ordenes.montos.total')}
        info={t('admin.ordenes.montos.totalInfo')}
        decimales={6}
        ancho="corto"
      />
      <CampoAreaTexto
        form={form}
        nombre="reason"
        etiqueta={t('admin.ordenes.montos.motivo')}
        info={t('admin.ordenes.montos.motivoInfo')}
        obligatorio={finalizada}
        ancho="completo"
      />
    </Ventana>
  );
}
